/**
 * Sommaire déduit du contenu, qui suit la lecture.
 *
 * Deuxième occurrence du motif dans la branche : il est né dans la modale
 * d'aide (`InfoModal`), qui lisait ses `.info-section` pour en faire un plan
 * cliquable, et la page « Documentation de l'API » en réclame exactement le
 * même. Plutôt que de recopier la règle « la section active est la dernière
 * dont le haut est passé sous le bord », elle vit ici une seule fois, et les
 * deux surfaces la consomment.
 *
 * Le sommaire n'est JAMAIS déclaré à la main : il est relu depuis le DOM
 * rendu. Une section ajoutée à une aide ou à la documentation y entre d'elle
 * même, et ne peut pas se désaccorder du contenu.
 *
 * Deux régimes de défilement, parce que les deux appelants diffèrent :
 *  - un ÉLÉMENT défilant (la modale, dont le corps a son propre `overflow-y`) ;
 *  - la FENÊTRE (une page du back-office, où c'est le document qui défile —
 *    un conteneur défilant y casserait les en-têtes collants, cf. C37).
 * `offsetTop` ne convient qu'au premier cas ; le second se mesure au
 * `getBoundingClientRect()`. D'où `topOf()`, seul endroit qui connaisse la
 * différence.
 */

import { onBeforeUnmount, ref } from 'vue'

/** Marge au-dessus de laquelle une section compte comme « atteinte ». */
const DEFAULT_OFFSET = 28

export function useReadingToc({
  /** Ref de l'élément qui contient les sections. */
  root,
  /**
   * Ref de l'élément défilant, ou `null` pour la fenêtre. Une fonction est
   * acceptée pour les cas où l'élément n'existe pas encore au montage.
   */
  scroller = null,
  /** Sélecteur des blocs à indexer. */
  selector = '.info-lead, .info-section',
  /** Libellé d'un bloc. Par défaut : le texte de son premier titre. */
  labelOf = (el) => el.querySelector('h2, h3')?.textContent?.trim(),
  /** Préfixe des identifiants posés sur les blocs qui n'en ont pas. */
  idPrefix = 'toc-section',
  /** Marge de déclenchement du suivi de lecture. */
  offset = DEFAULT_OFFSET,
  /**
   * Marge laissée AU-DESSUS de la section quand on y défile. Réf ou nombre :
   * une page dont un élément flottant occupe le haut de l'écran doit en tenir
   * compte, sinon le titre visé arrive dessous. C'est le cas du bouton de menu
   * du back-office sur écran étroit — fixe, en haut à gauche, 48 px.
   */
  scrollOffset = 8
} = {}) {
  const sections = ref([])
  const activeId = ref(null)

  const resolveScroller = () => {
    const raw = typeof scroller === 'function' ? scroller() : scroller?.value ?? scroller
    return raw ?? null
  }

  /** Position d'un élément dans le repère du défilement en cours. */
  const topOf = (el, sc) => {
    if (sc) return el.offsetTop
    return el.getBoundingClientRect().top + window.scrollY
  }

  const scrollTopOf = (sc) => (sc ? sc.scrollTop : window.scrollY)

  /**
   * (Re)lit les blocs du contenu. `querySelectorAll` rend l'ordre du document,
   * donc l'ordre de lecture — jamais besoin de trier.
   */
  const readSections = () => {
    const el = root?.value
    if (!el) {
      sections.value = []
      activeId.value = null
      return
    }

    sections.value = Array.from(el.querySelectorAll(selector)).reduce((acc, node, index) => {
      const label = labelOf(node, index)
      if (!label) return acc
      // Un bloc qui porte déjà un id le garde : c'est peut-être une ancre
      // partagée ailleurs (lien profond, test).
      const id = node.id || `${idPrefix}-${index}`
      node.id = id
      acc.push({ id, label, el: node })
      return acc
    }, [])

    activeId.value = sections.value[0]?.id ?? null
  }

  const goTo = (id) => {
    const section = sections.value.find((s) => s.id === id)
    if (!section) return
    const sc = resolveScroller()
    const marge = typeof scrollOffset === 'number' ? scrollOffset : (scrollOffset?.value ?? 8)
    const target = Math.max(0, topOf(section.el, sc) - marge)

    if (sc) sc.scrollTo({ top: target, behavior: 'smooth' })
    else window.scrollTo({ top: target, behavior: 'smooth' })

    activeId.value = id
  }

  /**
   * La section active est la DERNIÈRE dont le haut est passé sous le bord :
   * le sommaire suit la lecture au lieu d'attendre un clic.
   */
  const syncActive = () => {
    if (sections.value.length === 0) return
    const sc = resolveScroller()
    const limit = scrollTopOf(sc) + offset

    let current = sections.value[0].id
    for (const section of sections.value) {
      if (topOf(section.el, sc) <= limit) current = section.id
    }
    if (current !== activeId.value) activeId.value = current
  }

  /**
   * Branche le suivi sur le défilement en cours. Rendu séparément de la
   * lecture des sections : la modale relit ses blocs à chaque ouverture, mais
   * n'écoute qu'une fois.
   */
  let detach = null

  const listen = () => {
    stop()
    const sc = resolveScroller()
    const target = sc ?? window
    target.addEventListener('scroll', syncActive, { passive: true })
    detach = () => target.removeEventListener('scroll', syncActive)
  }

  const stop = () => {
    if (detach) detach()
    detach = null
  }

  onBeforeUnmount(stop)

  return { sections, activeId, readSections, goTo, syncActive, listen, stop }
}
